<template>
  <div class="Page__PaymentResult">
    <div v-if="isSuccess" class="Module Module__Success">
      <div class="icon">
        <iconpark-icon name="circle-check"></iconpark-icon>
      </div>
      <div class="title">{{ $t('zhi-fu-cheng-gong') }}</div>
      <template v-if="orderInfo">
        <div v-if="orderInfo.order_type === ORDER_TYPE.STORAGE" class="desc">
          {{ orderInfo?.rights_name }}
          {{ $t('comma') }}
          {{ getFormateFileSize(orderInfo.rights.total_storage, false) }}
          Purchased
          {{ $t('comma') }}
          Cost
          {{ getFee() }}
          {{ getCoinName() }}
        </div>
        <div v-if="orderInfo.order_type === ORDER_TYPE.ACCOUNT" class="desc">
          {{ orderInfo?.rights_name }}
          {{ $t('comma') }}
          {{ orderInfo.rights.sub_account_num }} Member Seats Purchased
          {{ $t('comma') }}
          Cost
          {{ getFee() }}
          {{ getCoinName() }}
        </div>
        <div v-if="orderInfo.order_type === ORDER_TYPE.CREDITS" class="desc">
          {{ orderInfo?.rights_name }}
          {{ $t('comma') }}
          {{ orderInfo?.total_credit_coin }} Credits Purchased
          {{ $t('comma') }}
          Cost
          {{ getFee() }}
          {{ getCoinName() }}
        </div>
        <div v-if="orderInfo.order_type === ORDER_TYPE.PACKAGE" class="desc">
          {{ orderInfo?.rights_name }}
          {{
            orderInfo?.time_identity === '单月'
              ? $t('yue-tao-can')
              : $t('nian-tao-can')
          }}
          {{ $t('comma') }}
          Cost
          {{ getFee() }}
          {{ getCoinName() }}
        </div>
      </template>

      <div class="operation">
        <a-button type="primary" size="large" @click="refreshHome">
          {{ $t('hui-dao-you-yan') }}
        </a-button>
      </div>
    </div>
    <div v-else class="Module Module__Fail">
      <div class="icon">
        <iconpark-icon name="circle-close"></iconpark-icon>
      </div>
      <div class="title">{{ $t('zhi-fu-shi-bai') }}</div>
      <div class="desc">{{ $t('qing-zhong-xin-zhi-fu') }}</div>
      <div class="operation">
        <a-button
          v-if="orderInfo?.order_type === ORDER_TYPE.PACKAGE"
          type="primary"
          size="large"
          @click="navigateTo({ name: PageKey.SUBSCRIBE })"
        >
          {{ $t('zhong-xin-zhi-fu') }}
        </a-button>
        <a-button
          v-if="orderInfo?.order_type === ORDER_TYPE.CREDITS"
          type="primary"
          size="large"
          @click="refreshHome"
        >
          {{ $t('hui-dao-you-yan') }}
        </a-button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { getOrderClientDetail } from '~/api/module/rights.ts'
import { BUY_TYPE, ORDER_TYPE, USER_LEVEL_KEY } from '~/api/interface'
import type { IOrder } from '~/api/interface/order'
import { RIGHTS_TIME } from '~/constants'
import { PageKey } from '~/constants/page'
const { $t } = useNuxtApp()

const coinHans: Record<string, string> = {
  unknown: $t('wei-zhi-bi-zhong'),
  USD: $t('mei-yuan'),
  CNY: $t('ren-min-bi'),
  HKD: $t('gang-bi'),
}

const getCoinName = () => {
  return coinHans[orderInfo.value?.currency || 'unknown']
}

const getFee = () => {
  const v = Number(orderInfo.value?.pay_fee)
  return Number.isNaN(v) ? 0 : +v / 100
}

const isSuccess = !(getQueryString('type') === 'FAIL_URL')
const orderId = getQueryString('xmov_oid')

const orderInfo = ref<IOrder>()

if (orderId) {
  const { data } = await useAsyncData(() => getOrderClientDetail(+orderId))
  orderInfo.value = data.value?.data
  const record = data.value?.data

  if (isSuccess && record) {
    GAReport(GAEvent.PURCHASE, {
      currency: record.currency,
      value: record.pay_fee,
      transaction_id: String(record.id),
      items: [
        {
          item_id: String(record.rights_id),
          item_name: record.rights_name,
        },
      ],
    })
  }

  if (data.value?.data?.buy_type === BUY_TYPE.SUBSCRIBE) {
    if (import.meta.client) {
      const orderIdsStr = sessionGetItem('orderIds')
      let orderIds: string[] = []
      try {
        orderIds = orderIdsStr ? JSON.parse(orderIdsStr) : []
      } catch (error) {
        console.log(error)
      }

      if (!orderIds.includes(record?.pay_id || '')) {
        // 防止因为刷新，导致埋点触发多次
        orderIds.push(record!.pay_id)
        sessionSetItem('orderIds', JSON.stringify(orderIds))

        const $trackEvent = [
          USER_LEVEL_KEY.PERSONAL,
          USER_LEVEL_KEY.PERSONAL_LITE,
          USER_LEVEL_KEY.TASTE,
        ].includes(data.value.data.rights_type)
          ? TRACK_EVENT.PERSONAL_PACKAGE_PAYED
          : TRACK_EVENT.TEAM_PACKAGE_PAYED

        trackEvent($trackEvent, {
          PayTime: record?.create_time,
          PayMethod: record?.pay_type,
          expired_time: record?.expired_time,
          PayPackage: record?.time_identity === RIGHTS_TIME().month ? 1 : 2,
          pay_amount: record?.pay_fee,
          total_credit_coin: record?.total_credit_coin,
          buy_type: record?.buy_type,
        })
      }
    }
  }
}

const refreshHome = () => {
  location.href = location.origin + '/platform/'
}
</script>

<style lang="scss" scoped>
.Page__PaymentResult {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100vw;
  .Module {
    display: block;
    flex-direction: column;
    justify-content: center;
    > div {
      display: flex;
      justify-content: center;
    }
    .icon {
      font-size: 50px;
    }
    .title {
      font-weight: 500;
      font-size: 32px;
      color: var(--text-1);
      line-height: 45px;
      text-align: center;
      margin: 20px auto 12px;
    }
    .desc {
      font-size: 16px;
      color: var(--text-1);
      line-height: 24px;
      margin: 12px auto 72px;
    }
    &__Success {
      .icon {
        color: #29e69c;
      }
    }
    &__Fail {
      .icon {
        color: #ff5656;
      }
    }
  }
}
</style>
